export interface Article {
  id: string;
  title: string;
  slug: string;
  category: string;
  categorySlug: string;
  excerpt: string;
  content: string;
  coverImage: string;
  author: string;
  date: string;
  readTime: number;
  tags: string[];
  featured: boolean;
}

export const articles: Article[] = [
  {
    id: "article-1",
    title: "mogu 新手入门指南：从零开始快速上手",
    slug: "beginner-guide-quick-start",
    category: "使用教程",
    categorySlug: "tutorial",
    excerpt: "刚接触 mogu？不用担心！这篇全面指南将带你从下载安装到熟练使用，逐步掌握 mogu 的所有基础操作，让你在短时间内成为 mogu 高手。",
    content: `
<h2>第一步：注册与登录</h2>
<p>首先访问 mogu 官网，点击右上角的"注册"按钮。填写你的电子邮箱地址，设置一个安全的密码，完成邮箱验证后即可成功注册。如果你已有账号，直接点击"登录"输入账号密码即可进入 mogu 平台。</p>
<p>为了确保账户安全，建议你开启双重认证（2FA）。在"账户设置"中找到"安全设置"，按照提示绑定手机号或使用认证器应用完成设置。</p>

<h2>第二步：熟悉主界面</h2>
<p>登录后你会看到 mogu 的主工作台。左侧是导航菜单栏，包含"仪表盘"、"项目管理"、"数据分析"、"团队协作"等核心模块。中部是主要内容区域，右侧可以展开快捷面板查看通知和待办事项。</p>
<p>建议花 10 分钟逐一浏览各个模块的功能入口，对整体布局建立一个基本认知。你可以通过拖拽调整侧边栏宽度，也可以在设置中自定义导航菜单的顺序。</p>

<h2>第三步：创建第一个项目</h2>
<p>点击"项目管理"中的"新建项目"按钮，输入项目名称和描述。mogu 提供了多种项目模板可供选择，新手建议选择"空白项目"从头开始，以便更好地理解每个功能模块的作用。</p>
<p>创建项目后，你可以在项目中添加任务、上传文件、设置里程碑。每个任务支持指派负责人、设定截止日期和优先级标记。</p>

<h2>第四步：配置数据分析</h2>
<p>mogu 的数据分析功能非常强大。在项目中点击"数据分析"标签，选择你想要追踪的数据源。mogu 支持导入 CSV、Excel 文件，也可以直接连接数据库进行实时分析。</p>
<p>数据导入后，你可以使用拖拽式图表构建器创建可视化报表。支持柱状图、折线图、饼图、热力图等 20+ 图表类型。生成的报表可以一键分享给团队成员。</p>

<h2>第五步：邀请团队成员</h2>
<p>独木不成林！在"团队管理"页面，点击"邀请成员"，输入同事的邮箱地址即可发送邀请。你可以为每位成员设置不同的权限角色：管理员、编辑者、查看者。</p>
<p>团队成员加入后，你们可以实时协作编辑文档、讨论任务进展、共享数据分析结果。所有的协作记录都会被完整保存，方便追溯。</p>

<h2>总结</h2>
<p>通过以上五个步骤，你已经基本掌握了 mogu 的核心操作流程。随着使用深入，你会发现更多高级功能和技巧。mogu 的官方帮助中心提供了详细的文档和视频教程，遇到问题可以随时查阅。</p>
`,
    coverImage: "https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20with%20laptop%20displaying%20dashboard%20interface%2C%20clean%20light%20background%20with%20soft%20shadows%2C%20warm%20ambient%20lighting%2C%20professional%20editorial%20photography%2C%20neutral%20cream%20and%20soft%20gray%20color%20palette%2C%20productive%20atmosphere&width=800&height=450&seq=article-cover-01&orientation=landscape",
    author: "mogu 官方团队",
    date: "2026-08-05",
    readTime: 8,
    tags: ["入门教程", "新手", "快速上手"],
    featured: true,
  },
  {
    id: "article-2",
    title: "mogu 智能分析功能深度解析：让数据说话",
    slug: "smart-analytics-deep-dive",
    category: "功能介绍",
    categorySlug: "features",
    excerpt: "mogu 的智能分析引擎是其核心竞争力之一。本文将深入剖析各项分析功能、算法原理以及如何利用它们为业务决策提供有力支撑。",
    content: `
<h2>智能分析引擎概述</h2>
<p>mogu 的智能分析引擎采用了先进的机器学习算法，能够自动识别数据中的模式、趋势和异常。与传统的数据分析工具不同，mogu 不仅展示数据，更能提供可执行的洞察建议。</p>

<h2>核心分析功能</h2>
<h3>1. 趋势预测</h3>
<p>基于历史数据，mogu 可以预测未来 30 天、90 天甚至一年的趋势走向。预测模型会根据数据特征自动选择最适合的算法，预测准确率高达 92% 以上。</p>

<h3>2. 异常检测</h3>
<p>当你的数据出现异常波动时，mogu 会在第一时间发出预警通知。你可以自定义异常检测的敏感度和通知方式，确保不会错过任何重要信号。</p>

<h3>3. 关联分析</h3>
<p>自动发现不同指标之间的关联关系。例如，mogu 可以发现"用户活跃度"与"产品留存率"之间的正相关性，帮助你找到提升用户留存的关键驱动因素。</p>

<h3>4. 自然语言查询</h3>
<p>不需要编写复杂的 SQL 语句，直接用中文输入你的问题。比如输入"上个月销售额最高的产品是哪个？"，mogu 就会自动解析并返回答案。</p>

<h2>实际应用案例</h2>
<p>某电商平台使用 mogu 分析引擎后，通过用户行为分析发现了购物车的平均放弃率为 68%。通过 mogu 的建议，他们优化了结算流程，将放弃率降低到 45%，直接提升了 23% 的转化率。</p>

<h2>最佳实践建议</h2>
<p>1. 定期更新数据源，确保分析基于最新数据</p>
<p>2. 为常用的分析场景创建仪表盘模板，提高日常效率</p>
<p>3. 善用对比分析功能，将当前数据与历史同期进行对比</p>
<p>4. 结合团队协作功能，将分析结果分享给相关决策者</p>
`,
    coverImage: "https://readdy.ai/api/search-image?query=Abstract%20data%20visualization%20dashboard%20with%20colorful%20charts%20and%20graphs%20on%20dark%20background%2C%20modern%20analytics%20interface%20design%2C%20clean%20minimalist%20style%2C%20soft%20warm%20accent%20colors%2C%20professional%20business%20analytics%20atmosphere&width=800&height=450&seq=article-cover-02&orientation=landscape",
    author: "mogu 数据分析团队",
    date: "2026-08-03",
    readTime: 12,
    tags: ["数据分析", "智能分析", "功能介绍"],
    featured: true,
  },
  {
    id: "article-3",
    title: "mogu v3.8 版本重磅更新：全新界面与性能飞跃",
    slug: "version-3-8-release-notes",
    category: "最新版本",
    categorySlug: "updates",
    excerpt: "mogu v3.8 正式发布！本次更新带来了全新的用户界面设计、大幅提升的系统性能，以及多项用户期待已久的新功能。",
    content: `
<h2>版本概览</h2>
<p>mogu v3.8 是我们今年最重要的版本更新之一。经过三个月的精心打磨，我们在界面设计、性能优化和功能新增三个维度都取得了显著提升。</p>

<h2>全新界面设计</h2>
<p>v3.8 采用了全新的设计语言，界面更加简洁直观。重新设计的导航系统让功能入口更加清晰，减少了用户的学习成本。全新的暗色模式也在此版本中正式上线。</p>

<h2>性能提升</h2>
<p>页面加载速度提升了 45%，数据处理速度提升了 60%。我们重写了核心渲染引擎，现在即使是处理百万级数据也能保持流畅体验。</p>

<h2>新增功能</h2>
<h3>1. 批量操作</h3>
<p>支持一次性对多个项目、任务进行批量编辑、删除、导出等操作，大幅提升工作效率。</p>

<h3>2. 自定义工作流</h3>
<p>通过可视化的拖拽方式创建自动化工作流，支持条件判断、循环、定时触发等高级逻辑。</p>

<h3>3. 增强的搜索功能</h3>
<p>全新的全文搜索引擎，支持模糊搜索、高级筛选和搜索历史记录。</p>

<h2>升级指南</h2>
<p>桌面端用户打开 mogu 后会自动提示更新。移动端用户可在应用商店中搜索 mogu 进行手动更新。更新前建议备份重要数据。</p>
`,
    coverImage: "https://readdy.ai/api/search-image?query=Modern%20software%20update%20release%20concept%20with%20abstract%20geometric%20shapes%2C%20clean%20light%20background%20with%20gradient%20accents%2C%20minimalist%20tech%20design%20aesthetic%2C%20warm%20neutral%20tones%2C%20professional%20product%20announcement%20style&width=800&height=450&seq=article-cover-03&orientation=landscape",
    author: "mogu 产品团队",
    date: "2026-07-28",
    readTime: 6,
    tags: ["版本更新", "v3.8", "新功能"],
    featured: true,
  },
  {
    id: "article-4",
    title: "mogu 各平台下载与安装完整指南",
    slug: "download-installation-guide",
    category: "下载指南",
    categorySlug: "download",
    excerpt: "详细介绍 mogu 在 Windows、macOS、iOS 和 Android 四大平台上的下载方式与安装步骤，附带常见问题解决方案。",
    content: `
<h2>Windows 平台</h2>
<p>1. 访问 mogu 官网下载页面，点击 Windows 版本下载按钮</p>
<p>2. 下载完成后，双击 mogu-setup.exe 运行安装程序</p>
<p>3. 按照安装向导提示完成安装，建议保持默认安装路径</p>
<p>4. 安装完成后桌面会出现 mogu 快捷方式，双击即可启动</p>
<p>系统要求：Windows 10 及以上，64 位系统，4GB 以上内存</p>

<h2>macOS 平台</h2>
<p>1. 在官网下载页面选择 macOS 版本（支持 Intel 和 Apple Silicon）</p>
<p>2. 下载完成后打开 mogu.dmg 文件</p>
<p>3. 将 mogu 图标拖入 Applications 文件夹</p>
<p>4. 首次打开时需要在系统偏好设置中允许来自 mogu 的应用程序</p>
<p>系统要求：macOS 12.0 及以上</p>

<h2>iOS 平台</h2>
<p>1. 在 App Store 中搜索"mogu"</p>
<p>2. 点击"获取"按钮下载安装</p>
<p>3. 安装完成后在桌面找到 mogu 图标即可打开使用</p>
<p>系统要求：iOS 15.0 及以上</p>

<h2>Android 平台</h2>
<p>1. 在 Google Play 或各大应用商店搜索"mogu"</p>
<p>2. 点击安装按钮下载</p>
<p>3. 也可以从官网直接下载 APK 安装包（需要允许安装未知来源应用）</p>
<p>系统要求：Android 10.0 及以上</p>
`,
    coverImage: "https://readdy.ai/api/search-image?query=Multiple%20devices%20laptop%20tablet%20phone%20displaying%20the%20same%20app%20interface%2C%20cross%20platform%20compatibility%20concept%2C%20clean%20white%20studio%20background%2C%20minimalist%20product%20photography%2C%20warm%20and%20modern%20aesthetic&width=800&height=450&seq=article-cover-04&orientation=landscape",
    author: "mogu 技术支持",
    date: "2026-07-25",
    readTime: 5,
    tags: ["下载", "安装", "多平台"],
    featured: false,
  },
  {
    id: "article-5",
    title: "2026 年数字化转型趋势：mogu 如何助力企业升级",
    slug: "digital-transformation-trends-2026",
    category: "行业资讯",
    categorySlug: "news",
    excerpt: "2026 年数字化转型进入深水区。本文探讨最新趋势以及 mogu 平台如何帮助企业在这场变革中抢占先机。",
    content: `
<h2>数字化转型新阶段</h2>
<p>2026 年，全球数字化转型已从"可选"变为"必选"。据 Gartner 最新报告显示，超过 78% 的企业已将数字化转型列为核心战略，而 2023 年这一比例仅为 52%。</p>

<h2>三大趋势</h2>
<h3>1. AI 驱动的决策</h3>
<p>人工智能不再是辅助工具，而是企业决策的核心引擎。mogu 的智能分析功能正是基于这一理念设计，帮助企业从海量数据中提取可执行洞察。</p>

<h3>2. 无代码/低代码革命</h3>
<p>技术民主化趋势持续加速。mogu 提供的可视化工作流编辑器让非技术人员也能构建自动化流程，显著降低技术门槛。</p>

<h3>3. 协作边界消失</h3>
<p>远程办公和混合办公成为常态，团队协作工具的需求激增。mogu 的实时协作平台帮助企业打破地域限制。</p>

<h2>mogu 的解决方案</h2>
<p>面对这些趋势，mogu 持续迭代产品，确保企业在数字化转型的每个阶段都能获得有力支撑。从数据分析到团队协作，从自动化流程到安全合规，mogu 提供了一站式解决方案。</p>
`,
    coverImage: "https://readdy.ai/api/search-image?query=Futuristic%20digital%20transformation%20concept%20with%20abstract%20network%20connections%2C%20modern%20technology%20abstract%20background%2C%20clean%20minimalist%20design%2C%20warm%20neutral%20tones%20with%20soft%20gradients%2C%20professional%20corporate%20aesthetic&width=800&height=450&seq=article-cover-05&orientation=landscape",
    author: "mogu 行业研究院",
    date: "2026-07-20",
    readTime: 10,
    tags: ["数字化转型", "行业趋势", "企业升级"],
    featured: false,
  },
  {
    id: "article-6",
    title: "mogu 团队协作功能实战：提升远程办公效率",
    slug: "team-collaboration-remote-work",
    category: "使用教程",
    categorySlug: "tutorial",
    excerpt: "远程办公时代，团队协作效率至关重要。本文通过实际案例展示如何利用 mogu 的协作功能提升团队生产力。",
    content: `
<h2>远程协作的挑战</h2>
<p>远程办公虽然带来了灵活性，但也伴随着沟通不畅、进度不可见、文档管理混乱等问题。mogu 的协作功能正是为解决这些痛点而生。</p>

<h2>核心协作功能</h2>
<h3>1. 实时文档协作</h3>
<p>支持多人同时编辑同一文档，所有修改实时同步。内置评论和 @提醒功能，确保沟通高效有序。</p>

<h3>2. 项目管理看板</h3>
<p>可视化看板让任务状态一目了然。支持自定义列、标签、优先级和截止日期。</p>

<h3>3. 视频会议集成</h3>
<p>内嵌视频会议功能，无需切换应用。会议中可直接共享 mogu 中的项目和数据。</p>

<h2>实战案例</h2>
<p>某设计团队使用 mogu 后，项目交付周期缩短了 30%，团队满意度从 72% 提升至 91%。</p>
`,
    coverImage: "https://readdy.ai/api/search-image?query=Modern%20remote%20team%20collaboration%20workspace%20with%20digital%20screens%20showing%20project%20management%20interface%2C%20clean%20bright%20modern%20office%20background%2C%20professional%20teamwork%20concept%2C%20warm%20neutral%20color%20palette%2C%20editorial%20style&width=800&height=450&seq=article-cover-06&orientation=landscape",
    author: "mogu 官方团队",
    date: "2026-07-15",
    readTime: 7,
    tags: ["团队协作", "远程办公", "效率提升"],
    featured: false,
  },
  {
    id: "article-7",
    title: "mogu API 集成开发指南：快速对接企业系统",
    slug: "api-integration-guide",
    category: "功能介绍",
    categorySlug: "features",
    excerpt: "详细介绍 mogu 开放 API 的能力范围、认证方式以及如何在 30 分钟内完成与现有企业系统的对接。",
    content: `
<h2>API 概述</h2>
<p>mogu 提供全面的 RESTful API，覆盖用户管理、数据分析、项目管理、团队协作等所有核心功能模块。所有 API 均采用 OAuth 2.0 认证，确保数据安全。</p>

<h2>快速开始</h2>
<p>1. 在 mogu 开发者中心创建应用，获取 Client ID 和 Client Secret</p>
<p>2. 使用 OAuth 2.0 授权码流程获取 Access Token</p>
<p>3. 在请求头中携带 Bearer Token 调用 API</p>

<h2>核心 API 端点</h2>
<p>/api/v1/projects - 项目管理</p>
<p>/api/v1/analytics - 数据分析</p>
<p>/api/v1/teams - 团队管理</p>
<p>/api/v1/files - 文件管理</p>

<h2>SDK 支持</h2>
<p>mogu 提供 Python、JavaScript、Java、Go 四种语言的官方 SDK，可在 GitHub 上获取。</p>
`,
    coverImage: "https://readdy.ai/api/search-image?query=Abstract%20API%20integration%20concept%20with%20code%20snippets%20and%20connection%20lines%2C%20modern%20developer%20tool%20design%2C%20dark%20editor%20theme%20with%20colorful%20syntax%20highlighting%2C%20clean%20tech%20aesthetic%2C%20professional%20software%20development%20atmosphere&width=800&height=450&seq=article-cover-07&orientation=landscape",
    author: "mogu 开发者团队",
    date: "2026-07-10",
    readTime: 9,
    tags: ["API", "集成", "开发者"],
    featured: false,
  },
  {
    id: "article-8",
    title: "mogu v3.9 展望：AI 助手与更多智能功能即将上线",
    slug: "version-3-9-preview",
    category: "最新版本",
    categorySlug: "updates",
    excerpt: "抢先预览 mogu v3.9 的计划功能，包括 AI 智能助手、增强自动化引擎以及全新的移动端体验。",
    content: `
<h2>v3.9 路线图</h2>
<p>mogu v3.9 计划于 2026 年 9 月发布，我们正在全力以赴开发中。以下是已确认将包含的主要功能。</p>

<h2>AI 智能助手</h2>
<p>全新的 AI 助手将深度集成到 mogu 的各个模块中。你可以用自然语言与它交互，完成数据分析、报告生成、任务分配等操作。</p>

<h2>增强自动化引擎</h2>
<p>自动化工作流将支持更多触发器和操作类型，包括 Webhook 集成、定时任务、条件分支等高级功能。</p>

<h2>全新移动端体验</h2>
<p>移动端将进行彻底重构，提供与桌面端几乎一致的功能体验。新增离线模式，即使没有网络也能继续工作。</p>
`,
    coverImage: "https://readdy.ai/api/search-image?query=Futuristic%20AI%20assistant%20concept%20with%20abstract%20holographic%20interface%2C%20minimalist%20tech%20design%2C%20clean%20dark%20background%20with%20warm%20glowing%20elements%2C%20modern%20product%20preview%20aesthetic%2C%20professional%20technology%20atmosphere&width=800&height=450&seq=article-cover-08&orientation=landscape",
    author: "mogu 产品团队",
    date: "2026-07-05",
    readTime: 5,
    tags: ["版本前瞻", "AI", "v3.9"],
    featured: false,
  },
  {
    id: "article-9",
    title: "mogu 安全性白皮书：数据保护与隐私合规",
    slug: "security-whitepaper",
    category: "功能介绍",
    categorySlug: "features",
    excerpt: "全面了解 mogu 的安全架构设计，包括数据加密、访问控制、合规认证以及隐私保护措施。",
    content: `
<h2>安全架构概览</h2>
<p>mogu 的安全性设计遵循"纵深防御"原则，在网络层、传输层、应用层和数据层均部署了多重防护措施。</p>

<h2>数据加密</h2>
<p>所有数据在传输过程中使用 TLS 1.3 加密，存储时采用 AES-256 算法加密。密钥管理采用硬件安全模块（HSM）。</p>

<h2>访问控制</h2>
<p>支持基于角色的访问控制（RBAC），管理员可以精细控制每个用户的权限。支持单点登录（SSO）集成。</p>

<h2>合规认证</h2>
<p>mogu 已通过 ISO 27001、SOC 2 Type II 等国际安全认证，并完全符合 GDPR 和中国《个人信息保护法》的要求。</p>

<h2>隐私保护</h2>
<p>我们承诺不将用户数据用于任何未经授权的目的。所有数据存储和处理均可在用户选择的数据中心区域进行。</p>
`,
    coverImage: "https://readdy.ai/api/search-image?query=Abstract%20cybersecurity%20shield%20concept%20with%20digital%20lock%20and%20encryption%20patterns%2C%20clean%20minimalist%20design%2C%20warm%20safe%20color%20palette%2C%20professional%20security%20technology%20background%2C%20trustworthy%20corporate%20aesthetic&width=800&height=450&seq=article-cover-09&orientation=landscape",
    author: "mogu 安全团队",
    date: "2026-06-28",
    readTime: 11,
    tags: ["安全性", "数据保护", "隐私"],
    featured: false,
  },
  {
    id: "article-10",
    title: "从 Excel 到 mogu：数据管理的进化之路",
    slug: "from-excel-to-mogu",
    category: "使用教程",
    categorySlug: "tutorial",
    excerpt: "如果你还在用 Excel 管理数据，这篇文章将告诉你为什么 mogu 是更好的选择，以及如何顺利迁移。",
    content: `
<h2>Excel 的局限性</h2>
<p>当数据量超过 10 万行时，Excel 的性能开始显著下降。协作困难、版本混乱、缺乏自动化等问题在团队规模扩大后更加突出。</p>

<h2>mogu 的优势</h2>
<p>1. 支持亿级数据处理，毫秒级响应</p>
<p>2. 多人实时协作，无需手动合并版本</p>
<p>3. 内置 AI 分析，自动发现数据洞察</p>
<p>4. 完善的权限管理，数据安全可控</p>

<h2>迁移指南</h2>
<p>mogu 提供一键导入功能，支持 CSV、Excel、JSON 等多种格式。导入后自动识别数据类型，智能推荐最佳的可视化方案。</p>
`,
    coverImage: "https://readdy.ai/api/search-image?query=Modern%20data%20migration%20concept%20showing%20transition%20from%20spreadsheet%20to%20cloud%20platform%2C%20clean%20minimalist%20design%2C%20warm%20professional%20atmosphere%2C%20abstract%20digital%20transformation%20visual&width=800&height=450&seq=article-cover-10&orientation=landscape",
    author: "mogu 官方团队",
    date: "2026-06-20",
    readTime: 6,
    tags: ["数据管理", "Excel", "迁移"],
    featured: false,
  },
];

export const getArticlesByCategory = (slug: string): Article[] => {
  return articles.filter((a) => a.categorySlug === slug);
};

export const getArticleBySlug = (slug: string): Article | undefined => {
  return articles.find((a) => a.slug === slug);
};

export const getFeaturedArticles = (): Article[] => {
  return articles.filter((a) => a.featured);
};

export const getRelatedArticles = (currentSlug: string, categorySlug: string, limit: number = 3): Article[] => {
  return articles
    .filter((a) => a.slug !== currentSlug && a.categorySlug === categorySlug)
    .slice(0, limit);
};